import type { SanityClient } from '@sanity/client'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import type { TeamMemberProfileProps } from '~/components/atoms/TeamMemberProfile/TeamMemberProfile'
import ContributorList from '~/components/blocks/ContributorList/ContributorList'
import TeamListFilter from '~/components/blocks/TeamListFilter/TeamListFilter'
import TeamProfileList from '~/components/blocks/TeamProfileList/TeamProfileList'
import { resolveBlock } from '~/components/serializers'
import {
  image,
  internationalizedArrayField,
  internationalizedStringField,
} from '~/schemas/common'
import { onlyPublished } from '~/services/sanity'
import { addLanguageFiltering } from '~/utils/articles/translationHelpers'

import type { PageTemplateModule, TemplateParams } from './types'

interface TeamMemberProps extends TeamMemberProfileProps {
  teamOrder: number
  magazineRole?: string[]
}

interface ContributorGroup {
  role: string
  members: TeamMemberProps[]
}

interface DepartmentGroups {
  [key: string]: TeamMemberProps[]
}

interface TeamTemplateData {
  teamMemberList: TeamMemberProps[]
  contributorGroups: ContributorGroup[]
}

const departmentPriorityOrder = [
  'leadership',
  'specialists & operations',
  'marketing & editorial',
]

function sortDepartments(a: string, b: string) {
  let indexA = departmentPriorityOrder.indexOf(a.toLowerCase())
  let indexB = departmentPriorityOrder.indexOf(b.toLowerCase())

  if (indexA === -1) indexA = departmentPriorityOrder.length
  if (indexB === -1) indexB = departmentPriorityOrder.length

  return indexA - indexB
}

function orderTeamMembersByTeamOrder(teamMemberList: TeamMemberProps[]) {
  return teamMemberList.sort((a, b) => {
    // First, compare by teamOrder
    const orderA = a.teamOrder ?? Number.MAX_VALUE
    const orderB = b.teamOrder ?? Number.MAX_VALUE

    if (orderA !== orderB) {
      return orderA - orderB
    }

    // If teamOrder is the same, then sort alphabetically by name
    const nameA = a.name?.toLowerCase() ?? ''
    const nameB = b.name?.toLowerCase() ?? ''

    return nameA.localeCompare(nameB)
  })
}

/**
 * Team Template Component
 * Renders the our-people/team page with department groupings
 */
function TeamTemplate({ data }: { data: any }) {
  const { teamMemberList, contributorGroups, body } = data
  const blocks = body || []

  // Group team members within their respective department
  const groupedByDepartment: DepartmentGroups =
    teamMemberList?.reduce((acc: DepartmentGroups, member: TeamMemberProps) => {
      if (member.department && member.department.length > 0) {
        member.department.forEach((role: string) => {
          if (!acc[role]) {
            acc[role] = []
          }
          acc[role].push(member)
        })
      }
      return acc
    }, {} as DepartmentGroups) || {}

  const restBlocks = [...blocks]
  const topBlock = blocks[0]

  delete restBlocks[0]

  // Order the departments by the priority ordered array
  const orderedDepartmentsData = Object.keys(groupedByDepartment)
    .sort(sortDepartments)
    .reduce((obj: any, key) => {
      obj[key] = groupedByDepartment[key]
      return obj
    }, {})

  return (
    <>
      {topBlock && <div>{resolveBlock(topBlock, 1)}</div>}
      <TeamListFilter />
      <div id="the-team">
        {orderedDepartmentsData &&
          Object.keys(orderedDepartmentsData).length > 0 &&
          Object.keys(orderedDepartmentsData).map(group => (
            <TeamProfileList
              title={group}
              {...teamMemberList}
              list={orderTeamMembersByTeamOrder(orderedDepartmentsData[group])}
              key={`team-profile-${group}`}
            />
          ))}
      </div>
      <div id="the-contributors">
        {contributorGroups?.map((group: ContributorGroup, i: number) => (
          <AnimateIntoView
            type={AnimationType.fadeIn}
            key={`team-author-${group.role}`}
          >
            <ContributorList
              {...teamMemberList}
              list={group.members}
              title={group.role}
              noPaddingBottom={i < contributorGroups.length - 1}
              key={`team-author-${group.role}`}
            />
          </AnimateIntoView>
        ))}
      </div>
      {restBlocks && (
        <div>{restBlocks.map((block, i) => resolveBlock(block, i))}</div>
      )}
    </>
  )
}

/**
 * Team Page Template Module
 * Fetches all team members and groups them by department and magazine role
 */
export const teamTemplate: PageTemplateModule<TeamTemplateData> = {
  async fetchData(client: SanityClient, params: TemplateParams) {
    const { lang } = params

    const data = await client.fetch<{ teamMemberList: TeamMemberProps[] }>(
      `{
        "teamMemberList": *[_type == 'team' ${onlyPublished}] | order(teamOrder asc)[] {
          _id,
          name,
          department,
          teamOrder,
          defined(department[]->name) => {
            "department": department[]->name
          },
          magazineRole,
          "slug": slug.current,
          "jobTitle": ${internationalizedStringField('jobTitle')},
          phoneNumber,
          email,
          "description": ${internationalizedArrayField('description', lang)},
          ${image},
          "articleCount": count(*[_type == 'article' && author._ref == ^._id ${addLanguageFiltering('')}]),
          image2,
          "quote": {
            "text": select(defined(quote) => ${internationalizedStringField('quote')}, null),
            author,
          },
        }
      }`,
      { lang }
    )

    // Group and sort contributors by magazine role
    const initialGroups: DepartmentGroups = {}

    const groupedByMagazineRole = data.teamMemberList.reduce(
      (acc: DepartmentGroups, member: TeamMemberProps) => {
        if (member.magazineRole && member.magazineRole.length > 0) {
          member.magazineRole.forEach(role => {
            if (!acc[role]) {
              acc[role] = []
            }
            acc[role].push(member)
          })
        }
        return acc
      },
      initialGroups
    )

    const entries = Object.entries(groupedByMagazineRole) as [
      string,
      TeamMemberProps[],
    ][]

    const contributorGroups: ContributorGroup[] = entries
      .sort(([roleA], [roleB]) => roleA.localeCompare(roleB))
      .map(([role, members]) => ({
        role,
        members: orderTeamMembersByTeamOrder(members),
      }))

    return {
      teamMemberList: data.teamMemberList,
      contributorGroups,
    }
  },

  component: TeamTemplate,
}
